/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Input,
    makeStyles,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens,
} from '@fluentui/react-components';
import React, { useEffect, useMemo, useState } from 'react';
import type { HistoryItem } from '../../models/HistoryItem';
import type { ProjektItem } from '../../models/ProjektItem';
import { DataService } from '../../services/dataService';

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: tokens.spacingHorizontalL,
        gap: tokens.spacingVerticalL,
    },
    filterContainer: {
        display: 'flex',
        gap: tokens.spacingHorizontalM,
        alignItems: 'center',
        marginBottom: tokens.spacingVerticalM,
    },
    searchInput: {
        width: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '60px 20px',
        minHeight: '300px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        flex: 1,
        border: `1px solid ${tokens.colorNeutralStroke1}`,
        borderRadius: tokens.borderRadiusMedium,
    },
    colTimestamp: {
        width: '180px',
        minWidth: '180px',
    },
    colUser: {
        width: '150px',
        minWidth: '150px',
    },
    colAction: {
        width: '60%',
        minWidth: '300px',
    },
});

interface EckdatenHistoryProps {
    project: ProjektItem | null;
}

const EckdatenHistory: React.FC<EckdatenHistoryProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();

    const [historyEntries, setHistoryEntries] = useState<HistoryItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');

    const dataService = React.useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch history entries
    useEffect(() => {
        const fetchHistoryEntries = async () => {
            if (!dataService || !project?.projektID) {
                return;
            }

            try {
                setIsLoading(true);
                const entries = await dataService.getHistoryEntries(project.projektID);
                console.log('Fetched history entries:', entries);
                setHistoryEntries(entries || []);
            } catch (err) {
                console.error('Error loading history entries:', err);
                setHistoryEntries([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchHistoryEntries();
    }, [dataService, project?.projektID]);

    // Filter entries based on search text
    const filteredEntries = useMemo(() => {
        if (!filterText.trim()) {
            return historyEntries;
        }

        const searchLower = filterText.toLowerCase();
        return historyEntries.filter((entry: any) => {
            return (
                entry.aktion?.toLowerCase().includes(searchLower) ||
                entry.mitarbeiterName?.toLowerCase().includes(searchLower) ||
                entry.benutzername?.toLowerCase().includes(searchLower) ||
                entry.pointInTimeAsDateTimeString?.toLowerCase().includes(searchLower)
            );
        });
    }, [historyEntries, filterText]);

    if (!project) return null;

    return (
        <div className={styles.container}>
            {/* Filter Input */}
            <div className={styles.filterContainer}>
                <Input
                    placeholder="Filter by action, user, or timestamp..."
                    value={filterText}
                    onChange={(_, data) => setFilterText(data.value)}
                    className={styles.searchInput}
                />
            </div>

            {/* Loading State */}
            {isLoading && (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading project history..." />
                </div>
            )}

            {/* Empty State */}
            {!isLoading && historyEntries.length === 0 && (
                <div className={styles.emptyState}>
                    No history entries found
                </div>
            )}

            {/* History Table */}
            {!isLoading && filteredEntries.length > 0 && (
                <div className={styles.tableContainer}>
                    <Table
                        size="small"
                    >
                        <TableHeader>
                            <TableRow style={{ backgroundColor: tokens.colorNeutralBackground2, borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                                <TableHeaderCell className={styles.colTimestamp} style={{ fontWeight: 700 }}>Timestamp</TableHeaderCell>
                                <TableHeaderCell className={styles.colUser} style={{ fontWeight: 700 }}>User</TableHeaderCell>
                                <TableHeaderCell className={styles.colAction} style={{ fontWeight: 700 }}>Action</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredEntries.map((entry: any, index) => (
                                <TableRow key={index}>
                                    <TableCell className={styles.colTimestamp}>
                                        <TableCellLayout>
                                            {entry.pointInTimeAsDateTimeString || '-'}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell className={styles.colUser}>
                                        <TableCellLayout>
                                            {entry.mitarbeiterName || entry.benutzername || '-'}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell className={styles.colAction}>
                                        <TableCellLayout>
                                            {entry.aktion || '-'}
                                        </TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* No results after filtering */}
            {!isLoading && historyEntries.length > 0 && filteredEntries.length === 0 && (
                <div className={styles.emptyState}>
                    No history entries match your search
                </div>
            )}
        </div>
    );
};

export default EckdatenHistory;
